import axios from 'axios';
import {
  getAccessToken,
  refreshTokens,
  clearTokens,
  isFatalRefreshError,
} from './tokenManager';

const apiClient = axios.create({
  baseURL: '/api',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor — attach auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// Response interceptor — unwrap API envelope { success, data, pagination }
// and handle 401 with token refresh
apiClient.interceptors.response.use(
  (response) => {
    // Unwrap the standard API envelope so callers get the inner data directly.
    // response.data is the JSON body: { success: true, data: {...}, pagination?: {...} }
    const body = response.data;
    if (body && typeof body === 'object' && body.success !== undefined) {
      // Attach pagination to data if present
      response.data = body.data;
      if (body.pagination) {
        response.pagination = body.pagination;
      }
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status !== 401 || !originalRequest || originalRequest._retry) {
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    try {
      // refreshTokens() is single-flight, so a burst of simultaneous 401s all await
      // the same request rather than racing to rotate the refresh token.
      const newAccessToken = await refreshTokens();
      originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
      return apiClient(originalRequest);
    } catch (refreshError) {
      // Only bounce to login when the session is genuinely dead. A 429 from the rate
      // limiter or an offline blip must not throw the user out of a working session.
      if (isFatalRefreshError(refreshError)) {
        redirectToLogin();
      }
      return Promise.reject(refreshError);
    }
  },
);

function redirectToLogin() {
  clearTokens();
  // Avoid redirect loop if already on login page
  if (!window.location.pathname.includes('/login')) {
    const slug = localStorage.getItem('tenantSlug');
    window.location.href = slug ? `/t/${slug}/login` : '/superadmin/login';
  }
}

export default apiClient;
